import { Link } from '@cedarjs/router'
import {
  MaterialReactTable,
  MRT_RowSelectionState,
  MRT_SortingState,
  useMaterialReactTable,
  type MRT_ColumnDef,
} from 'material-react-table'

import { useLazyQuery, useMutation } from '@apollo/client'
import ArrowForwardOutlinedIcon from '@mui/icons-material/ArrowForwardOutlined'
import BookOutlinedIcon from '@mui/icons-material/BookOutlined'
import DifferenceOutlinedIcon from '@mui/icons-material/DifferenceOutlined'
import EditIcon from '@mui/icons-material/Edit'
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined'
import HistoryEduOutlinedIcon from '@mui/icons-material/HistoryEduOutlined'
import SyncIcon from '@mui/icons-material/Sync'
import LinkOffIcon from '@mui/icons-material/LinkOff'
import ViewSidebarOutlinedIcon from '@mui/icons-material/ViewSidebarOutlined'
import WorkOutlineOutlinedIcon from '@mui/icons-material/WorkOutlineOutlined'
import { Link as MuiLink, Typography } from '@mui/material'
import {
  getLastComplianceTimeString,
  getMaxNextDueString,
  getNextDueString,
} from '@wingwork/common/src/maintenanceItem'
import { formatDateForDisplayInUtc } from '@wingwork/common/src/helpers'
import dayjs from 'dayjs'
import { get } from 'lodash'
import {
  MaintenanceItem,
  MaintenanceItemFilters,
  RemainingValueFilter,
} from 'types/graphql'

import { routes, useLocation } from '@cedarjs/router'

import { getIntervalAndToleranceStringArray } from '@wingwork/common/src/maintenanceItem'
import ApiError from 'src/components/ApiError'
import DueStatusChip from 'src/components/DueStatusChip/DueStatusChip'
import Loading from 'src/components/Loading'
import { GET_SAVED_COLUMNS_CONFIG } from 'src/components/ManageTableColumnsModal'
import IconButton from 'src/components/MUI/IconButton'
import { HighlightedTypography } from 'src/components/MUI/StyledComponents'
import useAircraftList from 'src/hooks/requests/useAircraftRecords'
import usePaginatedMaintenanceItemList from 'src/hooks/requests/usePaginatedMaintenanceItemList'
import useMaintenanceItemTable, {
  TableVariant,
} from 'src/hooks/useMaintenanceItemTable'
import { useOrgName } from 'src/hooks/useOrgName'
import { inProgressValues } from 'src/utils/maintenanceItem'

import TypeAndDescriptionDisplay, {
  ChipData,
} from '../../TypeAndDescriptionDisplayV2'

import { useEffect, useMemo, useRef, useState } from 'react'
import AtaCodeCell from './AtaCodeCell'
import LineItemDetailsDrawer from './LineItemDetails/LineItemDetailsDrawerV2'
import Button from 'src/components/MUI/Button'
import WarningIcon from '@mui/icons-material/Warning'
import { gql } from '@apollo/client'
import { toast } from 'react-hot-toast'

const SYNC_CHILD_WITH_PARENT_MAINTENANCE = gql`
  mutation SyncChildWithParentMaintenance($childItemId: String!) {
    syncChildWithParentMaintenance(childItemId: $childItemId) {
      id
      parentId
      isNextDueOutOfSyncWithParent
    }
  }
`

const UPDATE_MAINTENANCE_ITEM_TRANSACTION = gql`
  mutation UpdateMaintenanceItemTransaction(
    $id: String!
    $input: UpdateMaintenanceItemTransactionInput!
  ) {
    updateMaintenanceItemTransaction(id: $id, input: $input) {
      id
      parentId
      isNextDueOutOfSyncWithParent
    }
  }
`

const HIGHLIGHT_CARD_FILTER_COLUMN_MAP = {
  adhocOnlyNotDue: [
    'tailNumber',
    'ataCode',
    'typeDescription',
    'lastComplianceDate',
    'actions',
  ],
}

export type SortOrder = 'asc' | 'desc'

interface TableWithDataProps {
  variant: TableVariant
  onSelectItem?: (id: string | string[], isSelect?: boolean) => void
  selectedAircraft?: string
  setQueryVariables?: (queryVariables: MaintenanceItemFilters) => void
  queryVariables?: MaintenanceItemFilters
  disableTaskSelection?: boolean
  taskViewMode?: 'simple' | 'advanced'
  setTableTitle?: (title: string) => void
  discriminator?:
    | ''
    | 'associatedTasks'
    | 'componentRelatedMaintenanceItems'
    | 'unscheduledTasks'
    | 'dueListPicker'
  onChildTaskSliderClick?: (row: MaintenanceItem) => void
  enableRowVirtualization: boolean
  tableHeightSx?: string
}

const TableWithData: React.FC<TableWithDataProps> = ({
  variant,
  onSelectItem,
  selectedAircraft,
  disableTaskSelection = false,
  taskViewMode = 'advanced',
  setQueryVariables,
  queryVariables = {
    isChildItem: false,
    onlyDueItems: true,
    parentId: null,
    showInProgress: true,
  },
  setTableTitle,
  discriminator = '',
  onChildTaskSliderClick,
  enableRowVirtualization,
  tableHeightSx = '100%',
}) => {
  const [sorting, setSorting] = useState<MRT_SortingState>([
    {
      id: 'nextDueStatus',
      desc: false,
    },
  ])

  const [detailedTaskView, setDetailedTaskView] = useState(undefined)
  const [detailedTaskViewTab, setDetailedTaskViewTab] = useState(0)
  const [openDetailedTaskView, setOpenDetailedTaskView] = useState(false)
  const [loadingOperations, setLoadingOperations] = useState<Set<string>>(
    new Set()
  )
  const orgName = useOrgName()

  const {
    duelistFiltersData,
    getSearchConfig,
    setEnabledColumns,
    showChildItems,
    selectedItemIds,
    unselectAll,
    selectMaintenanceItem,
    deselectMaintenanceItem,
    setTableRefreshKey,
  } = useMaintenanceItemTable(variant)

  const {
    filterDataBy,
    powerSearchTerms,
    fuzzySearchTerms,
    aircrafts: aircraftFilter,
    trackedBy,
    timeFrameAll,
    timeframeCycles,
    timeframeDays,
    timeframeHours,
    timeframeLandings,
    projectionFilterData,
    generalFilterData,
    ataCodes,
    tableRefreshKey,
  } = duelistFiltersData

  const {
    maintenanceItems,
    hasNextPage,
    initialLoading,
    error,
    loadMaintenanceItems,
    fetchNextMaintenancePage,
    paginationLoading,
    totalCount,
  } = usePaginatedMaintenanceItemList()

  const [
    getSavedColumnsConfig,
    { data: savedTableConfig, loading: tableColumnConfigLoading },
  ] = useLazyQuery(GET_SAVED_COLUMNS_CONFIG)

  const {
    loadAircraftList,
    loading: isAircraftListLoading,
    hasLoaded: hasAircraftListLoaded,
    aircrafts,
  } = useAircraftList()

  // Common mutation handler for maintenance item operations
  const createMutationHandler = (
    operationName: string,
    successMessage: string
  ) => ({
    onCompleted: () => {
      // Remove all loading operations of this type
      setLoadingOperations((prev) => {
        const newSet = new Set(prev)
        Array.from(prev).forEach((id) => {
          if (id.startsWith(operationName)) {
            newSet.delete(id)
          }
        })
        return newSet
      })
      setTableRefreshKey()
      toast.success(successMessage)
    },
    onError: (error: Error) => {
      // Remove all loading operations of this type
      setLoadingOperations((prev) => {
        const newSet = new Set(prev)
        Array.from(prev).forEach((id) => {
          if (id.startsWith(operationName)) {
            newSet.delete(id)
          }
        })
        return newSet
      })
      console.error(`Error ${operationName}:`, error)
      const errorMessage =
        error.message || `Failed to ${operationName.toLowerCase()}`
      toast.error(`Error: ${errorMessage}`)
    },
  })

  const [syncChildWithParent] = useMutation(
    SYNC_CHILD_WITH_PARENT_MAINTENANCE,
    createMutationHandler('sync', 'Successfully synchronized with parent task')
  )

  const [updateMaintenanceItemTransaction] = useMutation(
    UPDATE_MAINTENANCE_ITEM_TRANSACTION,
    createMutationHandler('remove', 'Successfully removed parent relationship')
  )

  useEffect(() => {
    loadAircraftList({ orgSlug: orgName })
    getSavedColumnsConfig({
      variables: {
        tableName: 'MaintenanceItems',
        orgSlug: orgName,
      },
      fetchPolicy: 'cache-first',
    })
  }, [])

  useEffect(() => {
    if (savedTableConfig?.feTableConfiguration) {
      setEnabledColumns(savedTableConfig.feTableConfiguration?.columns || [])
    }
  }, [savedTableConfig])

  const location = useLocation()

  const currentRequest = useRef<{
    controller: AbortController
    id: number
  } | null>(null)
  const requestCounter = useRef(0)

  useEffect(() => {
    const abortController = new AbortController()
    const requestId = ++requestCounter.current

    // Abort previous request if it exists (before starting new one)
    if (currentRequest.current) {
      currentRequest.current.controller.abort()
    }

    // Store new request as current
    currentRequest.current = { controller: abortController, id: requestId }

    const loadData = async () => {
      //Constructing Aircraft Filter

      // Only proceed if this is still the current request (prevents race condition with stale data)
      if (currentRequest.current?.id === requestId) {
        const aircraftIds = queryVariables?.aircraftIds
          ? queryVariables?.aircraftIds
          : Object.keys(aircraftFilter).filter(
              (aircraftId) => aircraftFilter[aircraftId]
            )
        //Constructing Remaining Value Filter
        let remainingValueFilter = {}
        if (timeframeDays.days > 0) {
          remainingValueFilter = {
            type: 'CALENDAR_DAYS',
            value: timeframeDays.days,
          }
        } else if (timeframeHours.hours > 0) {
          remainingValueFilter = {
            type: 'FLYING_HOURS',
            value: timeframeHours.hours,
          }
        } else if (timeframeCycles.cycles > 0) {
          remainingValueFilter = {
            type: 'CYCLES',
            value: timeframeCycles.cycles,
          }
        } else if (timeframeLandings.landings > 0) {
          remainingValueFilter = {
            type: 'LANDINGS',
            value: timeframeLandings.landings,
          }
        } else {
          remainingValueFilter = undefined
        }

        //Constructing Status Filter and Unscheduled Type Filter (Highlight Cards)
        let statuses = []
        const unscheduledType = queryVariables?.unscheduledType ?? []
        let onlyDueItems = queryVariables?.onlyDueItems ? true : false
        filterDataBy.forEach((filter) => {
          switch (filter) {
            case 'pastDueAndAog':
              statuses.push('OVERDUE')
              break
            case 'inToleranceAndComingDue':
              statuses.push('IN_TOLERANCE')
              statuses.push('APPROACHING_DUE')
              break
            case 'discrepanciesAndMel':
              // Empty the array
              unscheduledType.splice(0, unscheduledType.length)
              unscheduledType.push('DISCREPANCY')
              unscheduledType.push('MEL')
              break
            // These values needs to be in sync with stats backend.
            case 'adsb':
              // Empty the array
              unscheduledType.splice(0, unscheduledType.length)
              unscheduledType.push('AIRWORTHINESS_DIRECTIVE')
              unscheduledType.push('AD_SB')
              unscheduledType.push('SPECIAL_AD')
              unscheduledType.push('RECURRING_AD')
              unscheduledType.push('EMERGENCY_AD')
              unscheduledType.push('ALERT_SB')
              unscheduledType.push('MANDATORY_SB')
              unscheduledType.push('SERVICE_LETTER')
              unscheduledType.push('SERVICE_BULLETIN')
              break
            case 'ads':
              // Empty the array
              unscheduledType.splice(0, unscheduledType.length)
              unscheduledType.push('AIRWORTHINESS_DIRECTIVE')
              unscheduledType.push('AD_SB')
              unscheduledType.push('SPECIAL_AD')
              unscheduledType.push('RECURRING_AD')
              unscheduledType.push('EMERGENCY_AD')
              break
            case 'sbs':
              // Empty the array
              unscheduledType.splice(0, unscheduledType.length)
              unscheduledType.push('ALERT_SB')
              unscheduledType.push('MANDATORY_SB')
              unscheduledType.push('SERVICE_LETTER')
              unscheduledType.push('SERVICE_BULLETIN')
              break
            case 'adhocOnlyNotDue':
              statuses.push('NOT_DUE')
              break
            case 'adhocAndDuelist':
              onlyDueItems = false
              break
            default:
              break
          }
        })

        const { date, projectionGrid } = projectionFilterData
        const {
          statuses: projectionStatuses,
          cadenceType,
          ...otherGeneralFilters
        } = generalFilterData
        statuses = statuses.concat(projectionStatuses ?? [])
        const showDueInNextNDays =
          date !== undefined ? dayjs(date).diff(dayjs(), 'days') : undefined

        const { id: sortField, desc } = sorting?.[0]
        const sortOrder = desc ? 'desc' : 'asc'
        const updatedQueryVariables = {
          ...queryVariables,
          ...getSearchConfig().variables,
          showChildItems,
          onlyDueItems,
          aircraftIds,
          statuses,
          unscheduledType,
          sortOrder,
          sortField,
          showDueInNextNDays,
          // Intentional empty string check to avoid sending empty string to backend
          cadenceType: cadenceType === '' ? undefined : cadenceType,
          ...otherGeneralFilters,
          ataCodes,
          trackedBy,
        }
        if (remainingValueFilter) {
          updatedQueryVariables['remainingValueFilters'] = [
            remainingValueFilter as RemainingValueFilter,
          ]
        } else {
          // no-dd-sa:typescript-code-style/no-lonely-if
          if (projectionGrid.length > 0 && date !== undefined && date !== '') {
            updatedQueryVariables['remainingValueFilters'] = []
            projectionGrid.forEach((grid) => {
              // TODO Handle APU Hours
              if (grid.metric === 'APU Hours') return
              const projectionGridTypeMapping = {
                Landings: 'LANDINGS',
                Cycles: 'CYCLES',
                Hours: 'FLYING_HOURS',
              }
              updatedQueryVariables['remainingValueFilters'].push({
                type: projectionGridTypeMapping[grid.metric],
                value: grid.projection,
              } as RemainingValueFilter)
            })
          }
        }

        setQueryVariables?.(updatedQueryVariables)
        loadMaintenanceItems(
          updatedQueryVariables,
          true,
          abortController.signal,
          requestId
        )
      }
    }

    // Promise.resolve() pushes loadData to microtask queue
    // new request starts after all cleanup is complete or else the new request might get aborted before it starts
    Promise.resolve().then(loadData)
  }, [
    powerSearchTerms,
    fuzzySearchTerms,
    aircraftFilter,
    timeFrameAll,
    timeframeDays,
    timeframeHours,
    timeframeCycles,
    timeframeLandings,
    filterDataBy,
    sorting,
    projectionFilterData,
    location.search,
    showChildItems,
    generalFilterData,
    ataCodes,
    trackedBy,
    tableRefreshKey,
  ])

  const isColumnVisible = (column: string) => {
    const filteredAircrafts = Object.keys(aircraftFilter).filter(
      (key) => aircraftFilter[key]
    )
    if (
      column === 'tailNumber' &&
      (filteredAircrafts.length === 1 || Object.keys(aircrafts).length === 1)
    ) {
      let tailNumber = ''
      if (filteredAircrafts.length === 1) {
        tailNumber = aircrafts[filteredAircrafts[0]]?.tailNumber
      } else if (Object.keys(aircrafts).length === 1) {
        tailNumber = aircrafts[Object.keys(aircrafts)[0]]?.tailNumber
      }
      setTableTitle?.(
        tailNumber
          ? `${tailNumber} ${variant === 'duelist' ? 'Due' : 'Tasks'} List`
          : `${variant === 'duelist' ? 'Fleet Due' : 'Tasks'} List`
      )
      return false
    } else if (column === 'tailNumber') {
      setTableTitle?.(`${variant === 'duelist' ? 'Fleet Due' : 'Tasks'} List`)
    }
    if (filterDataBy.length === 0) return true
    return HIGHLIGHT_CARD_FILTER_COLUMN_MAP[filterDataBy[0]]
      ? HIGHLIGHT_CARD_FILTER_COLUMN_MAP[filterDataBy[0]].includes(column)
      : true
  }

  const onDetailTaskViewClick = (event, row) => {
    event.stopPropagation()
    setDetailedTaskView(row)
    setOpenDetailedTaskView(true)
  }

  const handleSyncWithParent = (maintenanceItem: MaintenanceItem) => {
    const operationId = `sync-${maintenanceItem.id}`
    setLoadingOperations((prev) => new Set(prev).add(operationId))

    syncChildWithParent({
      variables: { childItemId: maintenanceItem.id },
    })
  }

  const handleRemoveAsChild = (maintenanceItem: MaintenanceItem) => {
    const operationId = `remove-${maintenanceItem.id}`
    setLoadingOperations((prev) => new Set(prev).add(operationId))

    updateMaintenanceItemTransaction({
      variables: {
        id: maintenanceItem.id,
        input: {
          maintenanceItem: {
            parentId: null, // Remove the parent relationship
          },
        },
      },
    })
  }

  const columns = useMemo<MRT_ColumnDef<MaintenanceItem>[]>(
    () => [
      {
        accessorKey: 'tailNumber',
        header: 'Tail No.',
        size: 150,
        enableSorting: false,
        Cell: ({ row }) => {
          const { original } = row
          const aircraft = aircrafts[original.aircraftId]

          return (
            <div className="flex flex-col">
              <MuiLink
                variant="body1"
                color="inherit"
                component={Link}
                to={
                  aircraft
                    ? routes.planeDashboard({
                        orgName: orgName,
                        id: original.aircraftId,
                      })
                    : routes.componentDetail({
                        orgName: orgName,
                        id: original.trackedByComponentId,
                      })
                }
              >
                <HighlightedTypography variant="body2">
                  {aircraft
                    ? aircraft.tailNumber
                    : original.trackedByComponent?.serialNumber}
                </HighlightedTypography>
              </MuiLink>
              <Typography variant="caption">
                {aircraft
                  ? aircraft.model
                  : original.trackedByComponent?.primaryComponentType}
              </Typography>
            </div>
          )
        },
      },
      {
        accessorKey: 'ataCode',
        header: 'ATA Code',
        size: 250,
        Cell: ({ row }) => <AtaCodeCell row={row.original} />,
      },
      {
        accessorKey: 'typeDescription',
        header: 'Type/Description',
        minSize: 400,
        grow: true,
        Cell: ({ row }) => {
          const { enabledColumns } = useMaintenanceItemTable(variant)
          const aircraftComponent = get(row.original, ['aircraftComponent'], {})
          const partNumber = get(aircraftComponent, 'partNumber')
          const serialNumber = get(aircraftComponent, 'serialNumber')
          const chips: ChipData[] = [
            ...(row.original.disposition
              ? [
                  {
                    label: row.original.disposition,
                  },
                ]
              : []),
            ...(row.original.childCount > 0
              ? [
                  {
                    label: 'Parent',
                    onClick: (e) => {
                      e.stopPropagation()
                      setDetailedTaskViewTab(1)
                      onDetailTaskViewClick(e, row.original)
                    },
                  },
                ]
              : []),
            ...(row.original.parentId !== null
              ? [
                  {
                    label: 'Child',
                    ...(row.original.isNextDueOutOfSyncWithParent
                      ? {
                          icon: <WarningIcon fontSize="small" />,
                          tooltip: 'Due status is out of sync with parent task',
                        }
                      : {}),
                  },
                ]
              : []),
            ...row.original.tags.map((tag) => ({
              label: tag.name,
            })),
          ]
          const tailTags = []
          if (!enabledColumns.includes('partSerial')) {
            if (partNumber) tailTags.push(`PN ${partNumber}`)
            if (serialNumber) tailTags.push(`SN ${serialNumber}`)
          }
          return (
            <div className="w-full">
              <TypeAndDescriptionDisplay
                description={row.original.title}
                chips={chips}
                highlight
                tailTags={tailTags.length > 0 ? tailTags : undefined}
              />
            </div>
          )
        },
      },
      {
        accessorKey: 'lastComplianceDate',
        header: 'Compliance',
        size: 150,
        Cell: ({ row }) => {
          const lastComplianceTimeArray = getLastComplianceTimeString(
            row.original
          )

          return (
            <div>
              {lastComplianceTimeArray.map((val, index) => (
                <Typography key={index} variant="body2">
                  {val}
                </Typography>
              ))}
            </div>
          )
        },
      },
      {
        accessorKey: 'interval',
        header: 'Interval',
        size: 150,
        enableSorting: false,
        Cell: ({ row }) => {
          const cadenceValueStringArray = useMemo(() => {
            return getIntervalAndToleranceStringArray(row.original.cadenceValue)
          }, [row.original.id])

          if (cadenceValueStringArray.length === 0) {
            return <Typography> ---- </Typography>
          }

          return (
            <div>
              {cadenceValueStringArray.map(([interval, tolerance], index) => (
                <div className="inline-block w-full" key={index}>
                  <Typography
                    component="span"
                    className="inline-block whitespace-nowrap"
                    variant="body2"
                  >
                    {interval}
                  </Typography>
                  {tolerance && (
                    <Typography
                      variant="caption"
                      component="span"
                      className="ml-0.5 inline-block"
                    >
                      + {tolerance}
                    </Typography>
                  )}
                </div>
              ))}
            </div>
          )
        },
      },
      {
        accessorKey: 'nextDueStatus',
        header: 'Next Due',
        size: 120,
        Cell: ({ row }) => {
          const projectedDate = formatDateForDisplayInUtc(
            get(row.original, 'calculatedNextDueAt')
          )

          const nextDueString = getNextDueString(row.original)

          if (nextDueString.length === 0) {
            return <Typography> ---- </Typography>
          }

          return (
            <div
              data-tooltip-id="global-tooltip"
              data-tooltip-content={`Projected Date: ${projectedDate}`}
            >
              {nextDueString.map((item) => (
                <Typography
                  className="whitespace-nowrap"
                  key={item}
                  variant="body2"
                >
                  {item}
                </Typography>
              ))}
            </div>
          )
        },
      },
      {
        accessorKey: 'maxDue',
        header: 'Max Due',
        size: 120,
        enableSorting: false,
        Cell: ({ row }) => {
          const maxNextDueString = getMaxNextDueString(row.original)

          if (maxNextDueString.length === 0) {
            return <Typography> ---- </Typography>
          }

          return (
            <div>
              {maxNextDueString.map((item) => (
                <Typography
                  className="whitespace-nowrap"
                  key={item}
                  variant="body2"
                >
                  {item}
                </Typography>
              ))}
            </div>
          )
        },
      },
      {
        accessorKey: 'calculatedNextDueAt',
        header: 'Remaining',
        size: 120,
        Cell: ({ row }) => (
          <DueStatusChip
            maintenanceItem={row.original}
            aircraft={aircrafts[row.original.aircraftId]}
            useGlobalTooltip
          />
        ),
      },
      {
        accessorKey: 'partSerial',
        header: 'Part/Serial',
        size: 120,
        enableSorting: false,
        Cell: ({ row }) => {
          const aircraftComponent = get(row.original, ['aircraftComponent'], {})
          const partNumber = get(aircraftComponent, 'partNumber')
          const serialNumber = get(aircraftComponent, 'serialNumber')

          return (
            <div className="flex w-full flex-col">
              {partNumber && (
                <Typography
                  variant="caption"
                  component="div"
                  className="truncate"
                  data-tooltip-id="global-tooltip"
                  data-tooltip-content={`Part Number: ${partNumber}`}
                >
                  PN {partNumber}
                </Typography>
              )}
              {serialNumber && (
                <Typography
                  variant="caption"
                  component="div"
                  className="mt-1 truncate"
                  data-tooltip-id="global-tooltip"
                  data-tooltip-content={`Serial Number: ${serialNumber}`}
                >
                  SN {serialNumber}
                </Typography>
              )}
            </div>
          )
        },
      },
      {
        accessorKey: 'actions',
        header: 'Actions',
        grow: false,
        muiTableHeadCellProps: {
          align: 'right',
        },
        enableSorting: false,
        Cell: ({ row }) => {
          const {
            isLedgerInProgress,
            isRevisionInProgress,
            isWorkOrderInProgress,
            isLogbookInProgress,
            isLogbookAwaitingSignature,
          } = inProgressValues(row.original)
          return (
            <div className="flex w-full items-center justify-end gap-0.5">
              {discriminator !== 'dueListPicker' && (
                <>
                  {isLedgerInProgress && (
                    <IconButton
                      id={`open-compliance-button-${row.original.id}`}
                      tooltip="Open compliance"
                      useGlobalTooltip
                      component={Link}
                      to={routes.bulkCompliance({
                        orgName: orgName,
                        ledgerId: row.original.complianceLedger?.id,
                      })}
                    >
                      <FolderOutlinedIcon />
                    </IconButton>
                  )}
                  {isRevisionInProgress && (
                    <IconButton
                      id={`continue-revision-button-${row.original.id}`}
                      tooltip="Continue revision"
                      useGlobalTooltip
                      component={Link}
                      to={routes.bulkCompliance({
                        orgName,
                        ledgerId: row.original.complianceLedger?.id,
                      })}
                    >
                      <DifferenceOutlinedIcon />
                    </IconButton>
                  )}
                  {isWorkOrderInProgress && (
                    <IconButton
                      id={`open-work-order-button-${row.original.id}`}
                      tooltip="Open work order"
                      useGlobalTooltip
                      component={Link}
                      to={routes.workOrder({
                        orgName,
                        id: row.original.workOrderIdInProgress,
                      })}
                    >
                      <WorkOutlineOutlinedIcon />
                    </IconButton>
                  )}
                  {isLogbookInProgress && (
                    <IconButton
                      id={`open-logbook-button-${row.original.id}`}
                      tooltip="Open logbook"
                      useGlobalTooltip
                      component={Link}
                      to={routes.bulkCompliance({
                        orgName,
                        ledgerId: row.original.complianceLedger?.id,
                      })}
                    >
                      <BookOutlinedIcon />
                    </IconButton>
                  )}
                  {isLogbookAwaitingSignature && (
                    <IconButton
                      id={`open-sign-logbook-button-${row.original.id}`}
                      tooltip="E-sign logbook"
                      useGlobalTooltip
                      component={Link}
                      to={routes.bulkCompliance({
                        orgName,
                        ledgerId: row.original.complianceLedger?.id,
                      })}
                    >
                      <HistoryEduOutlinedIcon />
                    </IconButton>
                  )}
                  <IconButton
                    id={`open-sidebar-button-${row.original.id}`}
                    tooltip="Open sidebar"
                    useGlobalTooltip
                    onClick={(event) =>
                      onDetailTaskViewClick(event, row.original)
                    }
                  >
                    <ViewSidebarOutlinedIcon />
                  </IconButton>
                  {discriminator === 'associatedTasks' &&
                    row.original.parentId &&
                    row.original.isNextDueOutOfSyncWithParent && (
                      <IconButton
                        id={`sync-with-parent-button-${row.original.id}`}
                        tooltip="Sync with Parent"
                        useGlobalTooltip
                        onClick={() => handleSyncWithParent(row.original)}
                        disabled={loadingOperations.has(
                          `sync-${row.original.id}`
                        )}
                      >
                        {loadingOperations.has(`sync-${row.original.id}`) ? (
                          <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                        ) : (
                          <SyncIcon />
                        )}
                      </IconButton>
                    )}
                  {discriminator === 'associatedTasks' && (
                    <IconButton
                      id={`edit-task-button-${row.original.id}`}
                      tooltip="Edit Task"
                      useGlobalTooltip
                      onClick={() => onChildTaskSliderClick(row.original)}
                    >
                      <EditIcon />
                    </IconButton>
                  )}
                  {discriminator === 'associatedTasks' &&
                    row.original.parentId && (
                      <IconButton
                        id={`remove-as-child-button-${row.original.id}`}
                        tooltip="Remove as Child"
                        useGlobalTooltip
                        onClick={() => handleRemoveAsChild(row.original)}
                        disabled={loadingOperations.has(
                          `remove-${row.original.id}`
                        )}
                      >
                        {loadingOperations.has(`remove-${row.original.id}`) ? (
                          <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                        ) : (
                          <LinkOffIcon />
                        )}
                      </IconButton>
                    )}
                </>
              )}

              <IconButton
                id={`open-detail-page-button-${row.original.id}`}
                tooltip="Open detail page"
                useGlobalTooltip
                component={Link}
                to={routes.maintenanceItem({
                  orgName,
                  id: row.original.id,
                })}
              >
                <ArrowForwardOutlinedIcon />
              </IconButton>
            </div>
          )
        },
      },
    ],
    [variant, aircrafts]
  )

  const { enabledColumns } = useMaintenanceItemTable(variant)
  const allColumns = columns.map((col) => col.accessorKey)
  const computeColumnVisibility = (
    enabledColumns: string[],
    alwaysShownColumns: string[] = ['tailNumber', 'actions']
  ) => {
    // Start with all columns hidden
    const baseVisibility = allColumns.reduce((acc, col) => {
      acc[col] = false
      return acc
    }, {})

    // Enable columns that should be visible
    const visibleColumns = enabledColumns.reduce((acc, col) => {
      acc[col] = isColumnVisible(col)
      return acc
    }, {})

    // Always show specific columns (unless explicitly hidden by isColumnVisible)
    const alwaysVisible = alwaysShownColumns.reduce((acc, col) => {
      acc[col] = isColumnVisible(col)
      return acc
    }, {})

    return {
      ...baseVisibility,
      ...visibleColumns,
      ...alwaysVisible,
    }
  }

  const [columnVisibility, setColumnVisibility] = useState({})

  useEffect(() => {
    if (!hasAircraftListLoaded) return
    const newVisibility = computeColumnVisibility(enabledColumns)
    setColumnVisibility(newVisibility)
  }, [enabledColumns, aircraftFilter, hasAircraftListLoaded])

  const [rowSelection, setRowSelection] = useState<MRT_RowSelectionState>({})

  const table = useMaterialReactTable({
    columns,
    enableStickyHeader: true,
    data: maintenanceItems,
    enableMultiRowSelection: true,
    enableRowSelection: disableTaskSelection
      ? false
      : (row) =>
          selectedAircraft
            ? selectedAircraft === row.original.aircraftId
            : true,
    enablePagination: false,
    enableBottomToolbar: true,
    enableTopToolbar: Object.keys(rowSelection).length > 0,
    // Disable internal actions to prevent them from being shown
    enableToolbarInternalActions: false,
    // Disable alert banner which is default selection banner which is not customizable
    positionToolbarAlertBanner: 'none',
    // Disable select all button
    enableSelectAll: false,
    enableRowVirtualization,
    enableColumnActions: false,
    manualSorting: true,
    enableSortingRemoval: false,
    layoutMode: 'grid',
    rowVirtualizerOptions: (props) => ({
      ...props,
      overscan: 5,
      getScrollElement: () => document.querySelector('.MuiTableContainer-root'),
      estimateSize: () => 100, // Fixed row height
      measureSize: false, // Disable dynamic measurement
    }),
    muiTableHeadCellProps: {
      sx: {
        backgroundColor: '#fafafa',
        color: 'grey',
      },
    },
    muiTableBodyRowProps: ({ row }) => {
      const {
        isLedgerInProgress,
        isRevisionInProgress,
        isWorkOrderInProgress,
        isLogbookInProgress,
        isLogbookAwaitingSignature,
      } = inProgressValues(row.original)

      // at most only 1 is active at a time
      const toolTipContent = [
        isLedgerInProgress && 'Compliance in progress',
        isRevisionInProgress && 'Revision in progress',
        isWorkOrderInProgress && 'Work order in progress',
        isLogbookInProgress && 'Logbook in progress',
        isLogbookAwaitingSignature && 'Logbook awaiting signature',
      ]
        .filter(Boolean)
        .join('\n')

      return {
        'data-tooltip-id': 'global-tooltip',
        'data-tooltip-content': toolTipContent,
        onClick: (event) => {
          // Don't trigger row selection if clicking on action buttons
          if ((event.target as HTMLElement).closest('.MuiIconButton-root')) {
            return
          }
          // Don't toggle if user is dragging to select text
          if (window.getSelection()?.toString()) {
            return
          }
          // Toggle row selection
          row.toggleSelected(!row.getIsSelected())
        },
        sx: {
          height: '100px',
        },
      }
    },
    muiTableContainerProps: {
      sx: {
        height: tableHeightSx,
        overflow: 'auto',
        flex: '1 1 auto',
        display: 'flex',
        flexDirection: 'column',
      },
      onScroll: (event) => {
        const target = event.target as HTMLDivElement

        const scrollPosition = target.scrollTop + target.clientHeight
        const threshold = target.scrollHeight * 0.8 // Load when within 20% of bottom

        if (
          scrollPosition > threshold &&
          hasNextPage &&
          !initialLoading &&
          !paginationLoading
        ) {
          fetchNextMaintenancePage()
        }
      },
    },
    muiTablePaperProps: {
      elevation: 0,
      sx: {
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        flex: '1 1 auto',
        borderRadius: '8px',
        border: '1px solid rgba(224, 224, 224, 1)',
      },
    },
    muiTopToolbarProps: {
      sx: {
        position: 'sticky',
        top: 0,
        backgroundColor: 'background.paper',
        zIndex: 1,
      },
    },
    renderTopToolbarCustomActions: () => {
      const selectedCount = selectedItemIds.length

      if (selectedCount === 0) return null

      return (
        <div className="flex flex-col">
          <div className="flex items-center gap-2">
            <Typography>
              Selected {selectedCount} of {totalCount} total rows
            </Typography>
            <Button
              onClick={() => {
                setRowSelection({})
                unselectAll()
                onSelectItem?.([])
              }}
              size="small"
              color="primary"
              variant="text"
            >
              Clear
            </Button>
          </div>
        </div>
      )
    },
    muiBottomToolbarProps: {
      sx: {
        position: 'sticky',
        bottom: 0,
        backgroundColor: 'background.paper',
        zIndex: 1,
      },
    },
    renderBottomToolbarCustomActions: () => {
      return (
        <Typography className="flex items-center">
          Fetched {maintenanceItems.length} of {totalCount} total rows
        </Typography>
      )
    },
    getRowId: (row) => row.id,
    onRowSelectionChange: (updaterOrValue) => {
      // Get the new selection state
      const newRowSelection =
        typeof updaterOrValue === 'function'
          ? updaterOrValue(rowSelection)
          : updaterOrValue

      const removedIds = Object.keys(rowSelection).filter(
        (id) => !newRowSelection[id]
      )
      const addedIds = Object.keys(newRowSelection).filter(
        (id) => !rowSelection[id]
      )

      // Update internal row selection state first
      setRowSelection(newRowSelection)

      // Get selected IDs from the new selection state
      const selectedIds = Object.keys(newRowSelection).filter(
        (id) => newRowSelection[id]
      )

      // Get the full maintenance items for selected rows
      const selectedItems = maintenanceItems.filter((item) =>
        selectedIds.includes(item.id)
      )

      const removedItems = maintenanceItems.filter((item) =>
        removedIds.includes(item.id)
      )

      // Update redux state
      if (selectedIds.length > 0) {
        if (addedIds.length > 0) {
          selectMaintenanceItem(selectedItems)
        }
        if (removedIds.length > 0) {
          deselectMaintenanceItem(removedItems)
        }
        // Notify parent component
        onSelectItem?.(selectedIds)
      } else {
        unselectAll()
        onSelectItem?.([])
      }
    },
    state: {
      rowSelection,
      sorting,
      isLoading:
        initialLoading || isAircraftListLoading || tableColumnConfigLoading,
      showProgressBars: paginationLoading,
      showSkeletons: false,
      columnVisibility,
      columnOrder: [
        'mrt-row-select',
        'tailNumber',
        ...enabledColumns,
        'actions',
      ],
      columnPinning: {
        right: ['actions'],
        left: ['mrt-row-select'],
      },
    },
    onSortingChange: setSorting,
  })

  useEffect(() => {
    if (selectedItemIds.length === 0 && Object.keys(rowSelection).length > 0) {
      setRowSelection({})
    }
  }, [selectedItemIds])

  // useEffect(() => {
  //   if (tableRefreshKey > 0) {
  // }, [tableRefreshKey])

  if (initialLoading || isAircraftListLoading || tableColumnConfigLoading)
    return <Loading />

  if (error) return <ApiError />

  return (
    <div className="flex-1 overflow-hidden">
      <MaterialReactTable table={table} />

      <LineItemDetailsDrawer
        open={openDetailedTaskView}
        onClose={() => {
          setOpenDetailedTaskView(false)
        }}
        parentTask={detailedTaskView}
        aircraft={aircrafts[detailedTaskView?.aircraftId]}
        activeTab={detailedTaskViewTab}
        setActiveTab={setDetailedTaskViewTab}
        refetch={() => {
          table.resetRowSelection()
          loadMaintenanceItems(queryVariables, true)
        }}
      />
    </div>
  )
}

export default TableWithData
